/**
 * Tela /select-tenant — TH-54656.
 *
 * Apenas super_admin chega aqui (RequireAuth + AppLayout só renderizam
 * o conteúdo de quem precisa). Lista tenants do backend com busca
 * client-side por `name` ou `slug` e persiste a seleção em
 * `useTenantSelectionStore` (zustand + localStorage). Depois do select,
 * navega para "/" — o interceptor de fetch (`apiClient.ts`) passa a
 * injetar `X-Tenant-Id` automaticamente em /api/*.
 *
 * Paridade visual com a `SelectTenantScreen` do mobile
 * (`apps/mobile-android/.../ui/tenant/SelectTenantScreen.kt`): card por
 * tenant, ícone à esquerda, name em destaque + `slug · status` em sub.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Building2, Search, RefreshCw, LogOut } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import { useLogout } from "@/hooks/useLogout";
import { useTenantSelectionStore } from "@/stores/tenantSelectionStore";

interface Tenant {
  id: string;
  name: string;
  slug: string | null;
  status: string | null;
}

interface TenantsPagedResponse {
  data: Tenant[];
}

async function fetchTenants(): Promise<TenantsPagedResponse> {
  // per_page alto: o seletor carrega todos de uma vez (mesma estratégia
  // do mobile). Se passar de 100 clientes, virar paginação no futuro.
  const res = await fetch("/api/tenants?per_page=100", {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as TenantsPagedResponse;
}

export function SelectTenantPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const logout = useLogout();
  const select = useTenantSelectionStore((s) => s.select);
  const [query, setQuery] = useState("");

  const tenantsQuery = useQuery({
    queryKey: ["tenants", "select-tenant"],
    queryFn: fetchTenants,
    enabled: user?.role === "super_admin",
    staleTime: 30_000,
  });

  const visible = useMemo(() => {
    const tenants = tenantsQuery.data?.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.slug ?? "").toLowerCase().includes(q),
    );
  }, [tenantsQuery.data, query]);

  if (authLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-[var(--color-muted-foreground)]">
          carregando…
        </span>
      </main>
    );
  }

  // Defesa em profundidade: o `RequireAuth` já redireciona, mas se um
  // tenant_admin chegar aqui via URL direta, mostramos mensagem clara.
  if (user && user.role !== "super_admin") {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Esta tela é apenas para super_admin.
          </p>
          <Button
            variant="ghost"
            className="mt-3"
            onClick={() => navigate({ to: "/", replace: true })}
          >
            Voltar
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col bg-[var(--color-background)] text-[var(--color-foreground)]">
      <header className="flex h-[60px] items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-background)] px-4 md:px-7">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
            SUPER ADMIN
          </div>
          <div className="truncate text-[17px] font-semibold tracking-[-0.01em]">
            Selecionar cliente
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Atualizar lista"
          title="Atualizar"
          onClick={() => tenantsQuery.refetch()}
          disabled={tenantsQuery.isFetching}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${tenantsQuery.isFetching ? "animate-spin" : ""}`}
          />
          Atualizar
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Sair"
          title="Sair"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
        >
          <LogOut className="mr-2 h-4 w-4" />
          {logout.isPending ? "Saindo…" : "Sair"}
        </Button>
      </header>

      <section className="mx-auto w-full max-w-2xl flex-1 px-4 py-8 md:px-7">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Escolha o cliente em que vai operar. O sistema vai isolar todas as
          telas ao tenant selecionado — pode trocar a qualquer momento pelo
          chip no topo.
        </p>

        <label className="mt-6 block">
          <span className="sr-only">Buscar cliente</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
            <Input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou slug…"
              className="pl-9"
              aria-label="Buscar cliente"
            />
          </div>
        </label>

        <div className="mt-6">
          {tenantsQuery.isLoading ? (
            <p className="text-center text-sm text-[var(--color-muted-foreground)]">
              Carregando clientes…
            </p>
          ) : tenantsQuery.error ? (
            <p className="text-center text-sm text-[var(--color-destructive)]">
              Erro ao buscar clientes — {(tenantsQuery.error as Error).message}
            </p>
          ) : visible.length === 0 ? (
            <p className="text-center text-sm text-[var(--color-muted-foreground)]">
              Nenhum cliente encontrado{query ? ` para "${query}"` : ""}.
            </p>
          ) : (
            <ul className="flex flex-col gap-2" role="list">
              {visible.map((tenant) => (
                <li key={tenant.id}>
                  <button
                    type="button"
                    onClick={() => {
                      select({ id: tenant.id, name: tenant.name });
                      navigate({ to: "/", replace: true });
                    }}
                    className="flex w-full items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-left transition-colors hover:bg-[var(--color-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
                    data-testid={`tenant-option-${tenant.id}`}
                  >
                    <span
                      aria-hidden
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary-soft)] text-[var(--color-primary)]"
                    >
                      <Building2 className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[var(--color-foreground)]">
                        {tenant.name}
                      </span>
                      <span className="block truncate font-mono text-[11px] text-[var(--color-muted-foreground)]">
                        {[tenant.slug, tenant.status]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}
